// De identiteitskleuren van avatars: EEN lijst voor elke plek die een kleur aan een
// nieuwe gebruiker toekent (lib/db.js, scripts/setup-owners.mjs,
// scripts/repair-owner-map.mjs). Losse kopieën liepen uiteen en gaven op 21 juli 2026
// twintig alumni exact dezelfde cyaan cirkel.
//
// De HV2-tokens zijn hier bewust GEEN bron: die dragen een betekenis (groen = goed,
// rood = fout, accentInt = interviewproces) en een persoon is geen status.
//
// Alleen voor de terugvalcirkel met initialen. Kiest een gebruiker zelf een kleur
// (avatar_colors, SettingsModal), dan wint die altijd, zie avatarFill() hieronder.
export const USER_COLORS = [
  '#0047ab', '#e8705a', '#16a34a', '#9333ea', '#ea580c',
  '#0891b2', '#be185d', '#ca8a04', '#0f766e', '#7c3aed',
];

// Vaste keuze uit het palet: dezelfde sleutel geeft altijd dezelfde kleur, dus een
// backfill die twee keer draait verplaatst niemand. FNV-1a, want er is hier niets
// cryptografisch aan; het gaat om een gelijkmatige spreiding over tien kleuren.
//
// Met tien kleuren en meer gebruikers dan dat botsen er onvermijdelijk een paar. Dat
// is de bedoelde uitkomst: geen twintig identieke cirkels meer, geen belofte van
// uniciteit.
export function pickUserColor(key) {
  const s = String(key == null ? '' : key);
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return USER_COLORS[h % USER_COLORS.length];
}

// De vulling van een avatar zonder foto. Kleur zonder gebruiker (een lege
// eigenaar, een '?'): de enige plek waar een neutrale cirkel nog bedoeld is.
export const NO_USER_FILL = '#cccccc';

// Een grijze kleur (r = g = b, ook #888 kort) is geen identiteit: zo kwam de
// DDL-default '#888888' op users.color terecht, en elke route die '#888888' als
// terugval invult, levert precies dat grijs aan.
export function isGreyColor(c) {
  const s = String(c == null ? '' : c).trim().toLowerCase();
  if (!s) return true;
  let m = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(s);
  if (m) return m[1] === m[2] && m[2] === m[3];
  m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/.exec(s);
  if (m) return m[1] === m[2] && m[2] === m[3];
  return false;
}

// DE REGEL voor de kleur achter de initialen (eigenaarsbesluit 2 oktober 2026: wie
// geen foto heeft, krijgt een eigen kleur, nooit grijs). Op volgorde:
//   1. avatar_colors, de eigen keuze uit Instellingen (oude rijen dragen een
//      kommalijst, alleen de EERSTE telt);
//   2. users.color, tenzij leeg of grijs;
//   3. pickUserColor(id), vast per gebruiker, gelijk aan de backfill in lib/db.js
//      (user_avatar_color_v1). Zonder id: de naam als sleutel.
// Neemt de gebruikersrij ({ id, name, color, avatar_colors }) en de
// gedenormaliseerde eigenaarvorm ({ userId, userName, userColor, userAvatarColors }).
// avatarColor() in components/HomeV2/lib.js en elke bordrij (avatarColor op
// /api/dealtune/board) lezen deze functie; lib/user-colors.test.mjs bewaakt dat.
export function avatarFill(user) {
  if (!user) return NO_USER_FILL;
  const raw = user.avatar_colors ?? user.avatarColors ?? user.userAvatarColors;
  if (raw) {
    const first = String(raw).split(',')[0].trim();
    if (first) return first;
  }
  const c = user.color ?? user.userColor;
  if (c && !isGreyColor(c)) return String(c).trim();
  const id = user.id ?? user.userId;
  if (id != null && String(id) !== '') return pickUserColor(String(id));
  const name = String(user.name ?? user.userName ?? '').trim();
  return name ? pickUserColor(name) : NO_USER_FILL;
}
